import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/shop';
import type { Favorite } from '../api/types';

/**
 * The customer's bookmarked partners. SOW §2.7.
 *
 * Removal is done HERE rather than by reusing the heart component, because the
 * consequence is different: on a deal card the heart is a toggle you can undo
 * with a second tap, and on this list un-favouriting makes the row disappear.
 * The row is removed from state immediately and restored if the call fails.
 */
export function FavoritesList() {
  const [items, setItems] = useState<Favorite[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    api.favorites()
      .then((r) => { if (!cancelled) setItems(r); })
      .catch(() => { if (!cancelled) setFailed(true); });

    return () => { cancelled = true; };
  }, []);

  async function remove(favorite: Favorite) {
    const before = items ?? [];

    setItems(before.filter((f) => f.businessId !== favorite.businessId));
    setNotice(null);

    try {
      await api.removeFavorite(favorite.businessId);
    } catch {
      setItems(before);
      setNotice('We could not remove that just now. Please try again.');
    }
  }

  if (failed) {
    return (
      <p className="notice error" role="alert">
        We could not load your favourites just now. Please try again.
      </p>
    );
  }

  if (items === null) {
    return (
      <div role="status">
        <span className="visually-hidden">Loading your favourites</span>
        <div className="skeleton" style={{ height: 120, borderRadius: 14 }} aria-hidden="true" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="acct-empty">
        <h2>No favourites yet</h2>
        <p className="muted">
          Tap the heart on any place you like and it will be waiting here — handy
          for the ones you go back to.
        </p>
        <Link to="/" className="btn">Browse offers</Link>
      </div>
    );
  }

  return (
    <>
      {notice !== null && <p className="notice" role="alert">{notice}</p>}

      <ul className="acct-list">
        {items.map((f) => (
          <li key={f.businessId}>
            <div className="acct-row">
              <span className="acct-row-main">
                <span className="acct-row-title">{f.businessName}</span>
                <span className="acct-row-sub">
                  {/* Locations first, because "which one is this" is usually a
                      question about where it is. Category is the fallback when
                      no branch has a city recorded. */}
                  {f.locations.length > 0
                    ? f.locations.join(' · ')
                    : f.category ?? 'Partner'}
                </span>
              </span>

              <span className="acct-row-side">
                {/*
                  ZERO IS SHOWN, not hidden. "Nothing on at the moment" is the
                  answer to why a favourite has gone quiet, and hiding the row
                  would leave the customer wondering where it went.
                */}
                {f.liveDealCount > 0 ? (
                  <Link to={`/?q=${encodeURIComponent(f.businessName)}`} className="btn small">
                    {f.liveDealCount === 1 ? '1 offer' : `${f.liveDealCount} offers`}
                  </Link>
                ) : (
                  <span className="acct-row-sub">Nothing on right now</span>
                )}

                <button
                  type="button"
                  className="btn secondary small"
                  onClick={() => void remove(f)}
                  aria-label={`Remove ${f.businessName} from your favourites`}
                >
                  Remove
                </button>
              </span>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
